// Main functionality for AI insurance advisor chatbot

import { PRODUCT_ID_MAP, SELECTORS } from "./constants";
import {
  displayMessage,
  setSendingState,
  setChatInputHeight,
  sendEvent, setChatbotPromptsState
} from "./utils";
import { handleDialogClose, liveChatState, sendMessageToZendesk, startZendeskChat} from "./zendesk";
import { SnowplowHelper } from "../../analytics/snowplow_helper";

// Sends a message to the AI insurance advisor and handles the response
function sendMessageToAi(wrapper, message, isPrompt) {
  if (message.trim().length === 0) return;

  const chatInput = wrapper.querySelector(SELECTORS.CHAT_INPUT);
  setChatInputHeight(chatInput);

  const sessionId = setSessionId(wrapper, chatInput);

  onFirstMessage(wrapper);
  displayMessage(wrapper, message, "user");
  setSendingState(wrapper, true);
  chatInput.value = "";

  const urlWithoutQueryParams = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  const urlParams = new URLSearchParams(window.location.search);

	urlParams.set("step_action", "recommendations_chat_api")
	urlParams.set("session_id", sessionId);
	if (chatInput.dataset.insProduct !== "No product") {
		urlParams.set("product", PRODUCT_ID_MAP[chatInput.dataset.insProduct].toLowerCase());
	}
	urlParams.set("message", message);
	urlParams.set("prompt_override", isPrompt);

	fetch(`${urlWithoutQueryParams}?${urlParams.toString()}`, {
		headers: {
			"Content-Type": "application/json",
			accept: "application/json",
		}
	}).then((response) => {
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return response.json();
	}).then((response) => {
		displayMessage(wrapper, response.response, "agent");
		if (!response.approved_answer && response.live_chat_available) {
			setSendingState(wrapper, false, true);
		} else {
			setSendingState(wrapper, false);
		}
	}).catch((error) => {
		console.error("Error sending message:", error);
		displayMessage(wrapper, "Sorry, something went wrong. Please try again.", "error");
		if (chatInput.dataset.insProduct === "No product") {
      setChatbotPromptsState(wrapper, false);
		}
		setSendingState(wrapper, false);
	});
}

function setSessionId(wrapper, chatInput) {
  let sessionId = wrapper.dataset.chatSessionId;
  const { insProduct } = chatInput.dataset;

  if (!sessionId) {
    sessionId = `${PRODUCT_ID_MAP[insProduct]}::${crypto.randomUUID()}`;
    wrapper.dataset.chatSessionId = sessionId;
  }

  return sessionId;
}

// Minimizes the drawer header and moves the agent intro message to the top of the chat messages
function onFirstMessage(wrapper) {
  const animateContainer = wrapper.closest(
    ".mobius-dialogue-modal__description.mobius-dialogue-modal__description_drawer",
  );
  const chatContainer = animateContainer?.querySelector(
    ".mobius-dialogue-modal__styled-drawer-content",
  );

  const messagesElem = wrapper.querySelector(SELECTORS.MESSAGES_ELEMENT);
  const isFirstMessage = !messagesElem.querySelector(".reco-chat-message");

  if (isFirstMessage && chatContainer) {
    const drawerContent = chatContainer.closest(".mobius-drawer__content");
    const drawerHeader = drawerContent?.previousElementSibling;
    if (drawerHeader?.classList.contains("mobius-drawer__header")) {
      drawerHeader.classList.add("minimized");
    }
  }

  const introElem = wrapper.querySelector(".reco-agent-intro");
  if (introElem) {
    const removedIntro = introElem.parentElement.removeChild(introElem);
    messagesElem.prepend(removedIntro);
  }
}

// ****
// EVENT HANDLERS
// ****

function handleQcpLiveChatClick(_e) {
  const dialog = document.querySelector(`.insurance-advisor-dialog[data-title="quote-comparison"]`)
  if (!dialog) return;

  const wrapper = dialog.querySelector(SELECTORS.CHAT_WRAPPER);
  if (!wrapper) return;

  dialog.querySelector('button.mobius-button').click();
  setChatbotPromptsState(wrapper, false);
  startZendeskChat(wrapper);
}

// Handle click events on the PCP prompts; shows the chat dialog and sends the selected prompt as a message

function handlePcpPromptClick(e) {
  const prompt = e.target.textContent.trim();
  const coverName = e.target.dataset.coverName;

  const dialog = document.querySelector(`.insurance-advisor-dialog[data-title="${coverName}"]`)
  const wrapper = dialog.querySelector(SELECTORS.CHAT_WRAPPER);

  // Send message and open the drawer
  // We can assume that there is no live chat in progress at this point, as closing the drawer will end it
  // But checking here for safety
  if (liveChatState !== "connected" && liveChatState !== "connecting") {
    sendMessageToAi(wrapper, prompt, true);
  }
  dialog.querySelector('button.mobius-button').click();
}

// Handle click event on start live chat button
function handleStartLiveChatClick(e) {
  const wrapper = e.target.closest(SELECTORS.CHAT_WRAPPER);
  if (!wrapper) return;

  sendEvent(wrapper, "ia_live_chat_start_clicked");

  startZendeskChat(wrapper);
}

// Handle click events on inline prompts; sends the clicked prompt as a message and removes the prompt from the list
function handleInlinePromptClick(e) {
  const prompt = e.target.textContent.trim();
  const wrapper = e.target.closest(SELECTORS.CHAT_WRAPPER);

  sendMessageToAi(wrapper, prompt, true);

  const nextElement = e.target.nextElementSibling;
  e.target.remove();
  if (nextElement && nextElement.tagName === "BR") {
    nextElement.remove();
  }
}

function handleChatSendEvent(e) {
  const wrapper = e.target.closest(SELECTORS.CHAT_WRAPPER);
  if (!wrapper) return;

  let input = null;
  if (e.target.matches(SELECTORS.SEND_BUTTON) && !e.target.classList.contains("disabled-input")) {
    input = wrapper.querySelector(SELECTORS.CHAT_INPUT);
  } else if (e.target.matches(SELECTORS.CHAT_INPUT)) {
    input = e.target;
  }

  if (!input) return;

  const message = input?.value;
  input.value = "";

  if (liveChatState === "connected") {
    sendMessageToZendesk(wrapper, message);
  } else if (liveChatState === "connecting") {
    console.warn("Cannot send message while liveChatState is 'connecting'");
  } else {
    sendMessageToAi(wrapper, message, false);
  }
}

function handleClicks(event) {
  if (event.target.matches('.mobius-chatbot-prompt')) {
    handlePcpPromptClick(event)
  } else if (event.target.dataset.liveChat) {
    handleStartLiveChatClick(event);
  } else if (event.target.matches(".reco-chat-prompts li")) {
    handleInlinePromptClick(event);
  } else if (event.target.matches(SELECTORS.SEND_BUTTON)) {
    handleChatSendEvent(event);
  }
}

// Handle input events in the chat input; enables/disables the send icon based on input content
function handleInputs(event) {
  if (event.target.matches(SELECTORS.CHAT_INPUT)) {
    const input = event.target;
    const inputWrapper = input.closest(".reco-chat-input-wrapper") || input.parentElement;
    const sendIcon = inputWrapper?.querySelector(SELECTORS.SEND_BUTTON);

    if (sendIcon && input.value.trim().length > 0) {
      sendIcon.classList.remove("disabled-input");
    } else if (sendIcon) {
      sendIcon.classList.add("disabled-input");
    }
  }
}

// Handle keypress events in the chat input; sends the message on Enter key press
function handleKeypresses(event) {
  if (
    event.key === "Enter" &&
    event.target.matches(SELECTORS.CHAT_INPUT)
  ) {
    event.preventDefault();
    if (event.target.value.length === 0) return;
    handleChatSendEvent(event);
  }
}

// Initialize event listeners when the DOM is fully loaded
document.addEventListener("DOMContentLoaded", () => {
  if (document.body.classList.contains("usa")) {
    return;
  }
  document.body.addEventListener("click", handleClicks);
  document.body.addEventListener("input", handleInputs);
  document.body.addEventListener("keypress", handleKeypresses);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const dialogs = node.matches(SELECTORS.DIALOG)
            ? [node]
            : node.querySelectorAll(SELECTORS.DIALOG);

          dialogs.forEach((dialog) => {
            dialog.addEventListener('close', handleDialogClose);
          });

          const detailsButton = node.matches('.live-chat-intervention__button')
            ? [node]
            : node.querySelectorAll('.live-chat-intervention__button');

          detailsButton.forEach((button) => {
            button.addEventListener('click', handleQcpLiveChatClick);
          });
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  setupSnowplow();
});

/*
The snowplowHelper instance is not available on React pages (as these are
expected to be sent using the Snowplow functionality in Mobius). Brie on the PCP
is a Frankensteinian mix of HAML and React, so we need to manually re-create
the snowplowHelper instance here to track Brie interaction.
*/
function setupSnowplow() {
  const snowplowConfigElement = document.getElementById("snowplow-tracking");

  let snowplowConfig = {};
  try {
    snowplowConfig = JSON.parse(snowplowConfigElement?.textContent || "{}");
  } catch (e) {
    snowplowConfig = {};
  }

  const snowplowHelper = snowplowConfig !== {}
    ? new SnowplowHelper(snowplowConfig)
    : null;

  if (snowplowHelper && !window.snowplowHelper) {
    window.snowplowHelper = snowplowHelper;
  }
}
